import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Search, Filter, X, FileText, Target, Package, 
  AlertTriangle, DollarSign, Users, Calendar 
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';
const API = BACKEND_URL;

const AdvancedSearch = ({ projectId, user }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [status, setStatus] = useState('all');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const performSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        query: searchQuery,
        entity_types: entityType,
        status: status
      });

      const response = await axios.get(
        `${API}/projects/${projectId}/search?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setEntityType('all');
    setStatus('all');
    setResults(null);
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'tasks': return <FileText className="h-4 w-4" />;
      case 'kpis': return <Target className="h-4 w-4" />;
      case 'deliverables': return <Package className="h-4 w-4" />;
      case 'risks': return <AlertTriangle className="h-4 w-4" />;
      case 'expenses': return <DollarSign className="h-4 w-4" />;
      case 'beneficiaries': return <Users className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const renderResultItem = (item, type) => {
    return (
      <Card key={item.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <div className="mt-1">
                {getEntityIcon(type)}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <Badge variant="outline" className="capitalize">
                    {type}
                  </Badge>
                  {item.status && (
                    <Badge className="capitalize">
                      {item.status.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900">
                  {item.title || item.name || 'Untitled'}
                </h4>
                {item.description && (
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {item.description}
                  </p>
                )}
                {item.due_date && (
                  <p className="text-sm text-gray-500 mt-1 flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    Due: {new Date(item.due_date).toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Advanced Search</h2>
        <p className="text-gray-600 mt-1">Search across all project data</p>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && performSearch()}
                  placeholder="Search tasks, KPIs, deliverables, beneficiaries..."
                  className="pl-10"
                />
              </div>
              <Button onClick={() => setShowFilters(!showFilters)} variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button onClick={performSearch} disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </Button>
              {results && (
                <Button onClick={clearSearch} variant="ghost">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Entity Type
                  </label>
                  <Select value={entityType} onValueChange={setEntityType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="tasks">Tasks</SelectItem>
                      <SelectItem value="milestones">Milestones</SelectItem>
                      <SelectItem value="kpis">KPIs</SelectItem>
                      <SelectItem value="deliverables">Deliverables</SelectItem>
                      <SelectItem value="risks">Risks</SelectItem>
                      <SelectItem value="expenses">Expenses</SelectItem>
                      <SelectItem value="beneficiaries">Beneficiaries</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Status
                  </label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      setEntityType('all');
                      setStatus('all');
                    }}
                  >
                    Reset Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle>
              Search Results ({results.total_count} {results.total_count === 1 ? 'item' : 'items'} found)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {results.total_count === 0 ? (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-600">No results found for "{results.query}"</p>
                <p className="text-sm text-gray-500 mt-2">Try different keywords or filters</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(results.results).map(([type, items]) => {
                  if (items.length === 0) return null;
                  return (
                    <div key={type}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 capitalize flex items-center">
                        {getEntityIcon(type)}
                        <span className="ml-2">{type} ({items.length})</span>
                      </h3>
                      <div className="space-y-2">
                        {items.map(item => renderResultItem(item, type))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedSearch;
